import { Check, ExternalLink, PencilIcon, X } from 'lucide-react';
import React from 'react';

import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { useInlineEdit } from '../hooks/useInlineEdit';

interface CMSLinkBadgeProps {
  matterId: string;
  cmsUrl?: string | null;
}

export const CMSLinkBadge: React.FC<CMSLinkBadgeProps> = ({
  matterId,
  cmsUrl,
}) => {
  const isValidUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return !!urlObj;
    } catch {
      return false;
    }
  };

  const {
    isEditing,
    setIsEditing,
    editValue,
    setEditValue,
    handleSave,
    handleCancel,
    isLoading,
  } = useInlineEdit({
    matterId,
    initialValue: cmsUrl || '',
    fieldName: 'external_cms_url',
    onSuccessMessage: 'CMS URL updated successfully',
    transformValue: (val) => {
      let urlToSave = val.trim();
      if (urlToSave && !urlToSave.match(/^https?:\/\//i)) {
        urlToSave = `https://${urlToSave}`;
      }
      return urlToSave || null;
    },
    validate: (val) => {
      let urlToSave = val.trim();
      if (!urlToSave) return true;
      if (!urlToSave.match(/^https?:\/\//i)) {
        urlToSave = `https://${urlToSave}`;
      }
      return isValidUrl(urlToSave) ? true : 'Please enter a valid URL';
    },
  });

  return (
    <div className="inline-flex items-center gap-0.5">
      {cmsUrl ? (
        <>
          <a
            href={cmsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs sm:text-[11px] lg:text-xs hover:bg-zinc-100 px-1.5 py-0.5 rounded transition-colors bg-zinc-50"
            aria-label="View in CMS"
          >
            <ExternalLink className="h-3 w-3" />
            <span className="font-medium">CMS</span>
          </a>
          <Popover
            open={isEditing}
            onOpenChange={(open) => {
              if (!open) handleCancel();
              else setIsEditing(true);
            }}
          >
            <PopoverTrigger asChild>
              <button
                onClick={() => setIsEditing(true)}
                className="h-6 w-6 hover:bg-zinc-100 rounded flex items-center justify-center transition-colors"
                aria-label="Edit CMS URL"
                type="button"
              >
                <PencilIcon className="h-3 w-3 text-zinc-500" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-2 w-auto" align="start" side="bottom">
              <div className="flex items-center gap-1">
                <div className="relative">
                  <Input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="example.com/matter/123"
                    className="pr-16 h-7 text-xs min-w-[180px] sm:min-w-[240px]"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSave();
                      if (e.key === 'Escape') handleCancel();
                    }}
                  />
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-0.5">
                    <button
                      onClick={handleSave}
                      disabled={isLoading}
                      className="h-5 w-5 hover:bg-green-100 rounded flex items-center justify-center"
                      type="button"
                    >
                      <Check className="h-3 w-3 text-green-600" />
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={isLoading}
                      className="h-5 w-5 hover:bg-red-100 rounded flex items-center justify-center"
                      type="button"
                    >
                      <X className="h-3 w-3 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </>
      ) : (
        <Popover
          open={isEditing}
          onOpenChange={(open) => {
            if (!open) handleCancel();
            else setIsEditing(true);
          }}
        >
          <PopoverTrigger asChild>
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-1 text-xs sm:text-[11px] lg:text-xs hover:bg-zinc-100 px-1.5 py-0.5 rounded transition-colors bg-zinc-50"
              aria-label="Add CMS Link"
              type="button"
            >
              <ExternalLink className="h-3 w-3" />
              <span className="font-medium">Add CMS Link</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-2 w-auto" align="start" side="bottom">
            <div className="flex items-center gap-1">
              <div className="relative">
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="example.com/matter/123"
                  className="pr-16 h-7 text-xs min-w-[180px] sm:min-w-[240px]"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave();
                    if (e.key === 'Escape') handleCancel();
                  }}
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-0.5">
                  <button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="h-5 w-5 hover:bg-green-100 rounded flex items-center justify-center"
                    type="button"
                  >
                    <Check className="h-3 w-3 text-green-600" />
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="h-5 w-5 hover:bg-red-100 rounded flex items-center justify-center"
                    type="button"
                  >
                    <X className="h-3 w-3 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
